import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Lock, ArrowLeft } from "lucide-react";

const ACCESS_CODE = "050226";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [error, setError] = useState(false);

  const handleCodeChange = (e) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, 6);
    setCode(val);
    setError(false);
    if (val.length === 6) {
      if (val === ACCESS_CODE) {
        setUnlocked(true);
        setError(false);
      } else {
        setError(true);
      }
    } else {
      setUnlocked(false);
    }
  };

  const handleGoogleLogin = () => {
    if (!unlocked) return;
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#0c0c1a", position: "relative" }}>
      {/* Back to landing */}
      <button
        onClick={() => navigate("/")}
        style={{
          position: "absolute", top: 24, left: 40, background: "none", border: "none",
          color: "rgba(255,255,255,0.5)", cursor: "pointer", display: "flex", alignItems: "center",
          gap: 6, fontSize: 13, fontFamily: "Manrope, sans-serif"
        }}
        data-testid="back-to-landing"
      >
        <ArrowLeft size={14} /> Back
      </button>

      <Card style={{ width: 400, textAlign: "center", background: "#14142b", border: "1px solid rgba(255,255,255,0.08)" }}>
        <CardContent style={{ padding: "48px 32px" }}>
          <img src="/toro-logo.png" alt="Toro Strategic" style={{ height: 56, margin: "0 auto 12px", display: "block", filter: "brightness(10)" }} />
          <p style={{ fontSize: 13, color: "rgba(255,255,255,0.4)", marginBottom: 32 }}>Development Appraisal Platform</p>

          {/* Access code input */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginBottom: 12 }}>
              <Lock size={13} style={{ color: unlocked ? "#22c55e" : "rgba(255,255,255,0.3)" }} />
              <span style={{ fontSize: 12, fontWeight: 600, color: unlocked ? "#22c55e" : "rgba(255,255,255,0.4)", fontFamily: "Manrope, sans-serif", letterSpacing: "0.05em" }}>
                {unlocked ? "ACCESS GRANTED" : "ENTER ACCESS CODE"}
              </span>
            </div>
            <div style={{ display: "flex", justifyContent: "center", gap: 8 }}>
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  style={{
                    width: 42, height: 50, borderRadius: 8,
                    border: `1.5px solid ${error && code.length === 6 ? "#ef4444" : code.length > i ? (unlocked ? "#22c55e" : "rgba(255,255,255,0.25)") : "rgba(255,255,255,0.1)"}`,
                    background: code.length > i ? "rgba(255,255,255,0.04)" : "transparent",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 20, fontWeight: 700, fontFamily: "Manrope, sans-serif",
                    color: unlocked ? "#22c55e" : "rgba(255,255,255,0.8)",
                    transition: "all 0.15s ease"
                  }}
                >
                  {code[i] || ""}
                </div>
              ))}
            </div>
            {/* Hidden actual input for typing */}
            <input
              type="text"
              inputMode="numeric"
              autoFocus
              value={code}
              onChange={handleCodeChange}
              maxLength={6}
              data-testid="access-code-input"
              style={{
                position: "absolute", opacity: 0, width: 1, height: 1, pointerEvents: "none"
              }}
            />
            {/* Clickable overlay to focus the hidden input */}
            <div
              onClick={() => document.querySelector('[data-testid="access-code-input"]')?.focus()}
              style={{ cursor: "text", marginTop: -50, height: 50, position: "relative", zIndex: 2 }}
            />
            {error && (
              <p style={{ fontSize: 12, color: "#ef4444", marginTop: 8 }} data-testid="code-error">
                Invalid access code
              </p>
            )}
          </div>

          {/* Google login button */}
          <Button
            onClick={handleGoogleLogin}
            disabled={!unlocked}
            style={{
              width: "100%", height: 44, fontSize: 14,
              background: unlocked ? "white" : "rgba(255,255,255,0.06)",
              color: unlocked ? "#0c0c1a" : "rgba(255,255,255,0.25)",
              border: unlocked ? "none" : "1px solid rgba(255,255,255,0.08)",
              cursor: unlocked ? "pointer" : "not-allowed",
              transition: "all 0.3s ease"
            }}
            data-testid="google-login-btn"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" style={{ marginRight: 10 }}>
              <path fill={unlocked ? "#4285F4" : "rgba(255,255,255,0.2)"} d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
              <path fill={unlocked ? "#34A853" : "rgba(255,255,255,0.2)"} d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill={unlocked ? "#FBBC05" : "rgba(255,255,255,0.2)"} d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill={unlocked ? "#EA4335" : "rgba(255,255,255,0.2)"} d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Sign in with Google
          </Button>
          <p style={{ fontSize: 11, color: "rgba(255,255,255,0.25)", marginTop: 24 }}>Secure authentication powered by Google</p>
        </CardContent>
      </Card>
    </div>
  );
}
